import { type RefObject } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { UploadFileProgress } from 'modules/files';
import spinnerTitle from 'components/others/spinnerTitle';
import AiInfoBlock from 'components/others/AiInfoBlock';
import {
    getCurrencyI18nParams,
    getIsLocalCurrencyMode,
} from 'utils/helpers/currency/currencyHelpers';
import SolarIcon from 'components/icons/SolarIcon';

interface ComingAiPanelProps {
    aiPricesInUsd: boolean;
    setAiPricesInUsd: (next: boolean) => void;
    aiBusy: boolean;
    comingProcess: boolean;
    rate: number;
    handleOpenClick: () => void;
    processingLabel: string;
    aiPhase: 'idle' | 'uploading' | 'processing';
    handleCancel: () => void;
    uploadProgress: number | boolean;
    refInputFile: RefObject<HTMLInputElement>;
    handleChangeFile: () => void;
}

const ComingAiPanel = ({
    aiPricesInUsd,
    setAiPricesInUsd,
    aiBusy,
    comingProcess,
    rate,
    handleOpenClick,
    processingLabel,
    aiPhase,
    handleCancel,
    uploadProgress,
    refInputFile,
    handleChangeFile,
}: ComingAiPanelProps) => {
    const { t } = useTranslation(['Warehouses']);
    const isLocalMode = getIsLocalCurrencyMode(rate);
    const currencyParams = getCurrencyI18nParams();

    return (
        <AiInfoBlock title={t('labelAiInfoTitle')}>
            <p>{t('labelAiInfo')}</p>
            {!isLocalMode && (
                <div className="form-check form-switch mb-3">
                    <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="warehouse-ai-prices-in-usd"
                        checked={aiPricesInUsd}
                        onChange={(e) => setAiPricesInUsd(Boolean(e.target.checked))}
                        disabled={aiBusy || comingProcess}
                    />
                    <label className="form-check-label" htmlFor="warehouse-ai-prices-in-usd">
                        {t('labelAiInvoiceCurrencyBase', currencyParams)}
                    </label>
                    {!aiPricesInUsd && (
                        <div className="small text-muted">
                            {t('labelAiInvoiceCurrencyConvertHint', {
                                ...currencyParams,
                                rate: Number(rate || 0).toFixed(2),
                            })}
                        </div>
                    )}
                </div>
            )}
            <div className="d-flex flex-wrap gap-2">
                <Button type="button" variant="primary" size="sm" onClick={handleOpenClick} disabled={aiBusy || comingProcess}>
                    {spinnerTitle(
                        <>
                            <SolarIcon name="stars" className="me-1" />
                            {t('labelAiOpenFile')}
                        </>,
                        aiBusy,
                        processingLabel,
                    )}
                </Button>
                {aiPhase === 'processing' && (
                    <Button type="button" variant="outline-danger" size="sm" onClick={handleCancel}>
                        <SolarIcon name="stop-circle" className="me-1" />
                        {t('btnAiCancel')}
                    </Button>
                )}
            </div>
            {uploadProgress !== false && (
                <div className="pt-2">
                    <UploadFileProgress progress={Number(uploadProgress)} />
                    <div className="small text-muted mt-2">{processingLabel}</div>
                </div>
            )}
            <input ref={refInputFile} type="file" className="d-none" onChange={handleChangeFile} />
        </AiInfoBlock>
    );
};

export default ComingAiPanel;
